/**
 * QrScannerModal — robust camera scanner using html5-qrcode.
 * IMPORTANT: stays mounted across open/close cycles to let cleanup run
 * BEFORE the scanner div is removed from the DOM. Toggles visibility only.
 */
import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const SCANNER_ID = "qr-scanner-region";

async function safeStop(scanner) {
    if (!scanner) return;
    try {
        if (scanner.isScanning) {
            await scanner.stop();
        }
    } catch (_) { /* already stopped or never started */ }
    try {
        scanner.clear();
    } catch (_) { /* noop */ }
}

export default function QrScannerModal({ open, onClose, onScan }) {
    const scannerRef = useRef(null);
    const handledRef = useRef(false);
    const cancelTokenRef = useRef({ cancelled: false });
    const [error, setError] = useState("");
    const [scanning, setScanning] = useState(false);

    useEffect(() => {
        if (!open) {
            // Close path: stop any active scan, do NOT unmount the DOM region
            const sc = scannerRef.current;
            scannerRef.current = null;
            cancelTokenRef.current.cancelled = true;
            safeStop(sc);
            setError("");
            setScanning(false);
            return;
        }

        // Open path
        const token = { cancelled: false };
        cancelTokenRef.current = token;
        handledRef.current = false;
        setError("");
        setScanning(true);

        (async () => {
            try {
                await new Promise((r) => setTimeout(r, 200));
                if (token.cancelled) return;
                const region = document.getElementById(SCANNER_ID);
                if (!region) {
                    setError("No se pudo inicializar el escáner.");
                    setScanning(false);
                    return;
                }
                const html5Qr = new Html5Qrcode(SCANNER_ID, { verbose: false });

                await html5Qr.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 250, height: 250 } },
                    (decoded) => {
                        if (token.cancelled || handledRef.current) return;
                        handledRef.current = true;
                        safeStop(html5Qr).finally(() => {
                            if (scannerRef.current === html5Qr) scannerRef.current = null;
                        });
                        try {
                            onScan?.(decoded);
                        } catch (e) {
                            console.error("onScan handler error:", e);
                        }
                    },
                    () => { /* ignore frame decode errors */ }
                );

                if (token.cancelled) {
                    await safeStop(html5Qr);
                    return;
                }
                scannerRef.current = html5Qr;
            } catch (e) {
                console.error("QR scanner start error:", e);
                if (!token.cancelled) {
                    const msg = String(e?.message || e || "").toLowerCase();
                    if (msg.includes("permission") || msg.includes("notallowed") || msg.includes("denied")) {
                        setError("Permiso de cámara denegado. Habilítalo en los ajustes del navegador.");
                    } else if (msg.includes("notfound") || msg.includes("no camera")) {
                        setError("No se detectó ninguna cámara en este dispositivo.");
                    } else if (msg.includes("https") || msg.includes("secure")) {
                        setError("La cámara requiere HTTPS.");
                    } else {
                        setError("No se pudo iniciar la cámara. Intenta cerrar y volver a abrir.");
                    }
                    setScanning(false);
                }
            }
        })();

        return () => {
            token.cancelled = true;
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Always render — toggle visibility via CSS to keep the scanner DOM region alive
    return (
        <div
            className={`fixed inset-0 z-[80] bg-black flex flex-col ${open ? "" : "invisible pointer-events-none"}`}
            data-testid="qr-scanner-modal"
            aria-hidden={!open}
        >
            <div className="flex items-center justify-between px-4 py-3 bg-black/70 text-white">
                <div className="flex items-center gap-2 font-semibold">
                    <Camera className="w-5 h-5" />
                    <span>Escanear QR</span>
                </div>
                <button onClick={onClose} className="p-1 hover:bg-white/10 rounded" data-testid="qr-scanner-close">
                    <X className="w-6 h-6" />
                </button>
            </div>

            <div className="relative flex-1 overflow-hidden">
                <div id={SCANNER_ID} className="w-full h-full" />
                {open && scanning && !error && (
                    <div className="absolute inset-0 pointer-events-none flex items-end justify-center pb-10">
                        <div className="bg-black/70 text-white text-sm px-4 py-2 rounded-full">
                            Apunta al código QR del dispositivo
                        </div>
                    </div>
                )}
                {open && error && (
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                        <div className="bg-white rounded-lg p-5 max-w-sm text-center">
                            <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-2" />
                            <p className="text-sm mb-3">{error}</p>
                            <Button onClick={onClose} variant="outline" data-testid="qr-scanner-error-close">Cerrar</Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
